import React, { useState, useEffect, useMemo } from "react";
import SurveysDamageForm from "./SurveysDamageForm";
import SurveysDamageEconomicLoss from "./SurveysDamageEconomicLoss";
import SurveysDamageUploads from "./SurveysDamageUploads";
import EmptyDamageForm from "./EmptyDamageForm";
import StaffDamageForm from "./StaffDamageForm";
import FixedAssetDamageForm from "./FixedAssetDamageForm";
import ProvisionDamageForm from "./ProvisionDamageForm";
import LivestockDamageForm from "./LivestockDamageForm";
import AgricultureProductDamageForm from "./AgricultureProductDamageForm";
import TlcComponentDamageForm from "../Telecommunication/TlcComponentDamageForm";
import TlcServiceDamageForm from "../Telecommunication/TlcServiceDamageForm";
import TransportServiceDamageForm from "../Transport/TransportServiceDamageForm";
import TransportComponentDamageForm from "../Transport/TransportComponentDamageForm";
import ElectricalServiceDamageForm from "../Electrical/ElectricalServiceDamageForm";
import ElectricalComponentDamageForm from "../Electrical/ElectricalComponentDamageForm";
import BuildingComponentDamageForm from "../Building/BuildingComponentDamageForm";
import DamageArtisticGoodForm from "../Building/DamageArtisticGoodForm";

import { elvis, search } from "../../Utils"
import { filterSendaiByEnterprise } from "../Enterprises/sendaiFilter";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

import { connect } from "react-redux";
import { Row, Col, Table, Button, Divider, Typography, Tooltip, notification } from "antd";
import ErrorBoundary from "./ErrorBoundary";
import axios from "axios";
import dayjs from 'dayjs';

import "./Survey.css";

const { Title } = Typography;

const SurveyDamagesInfo = props => {

  const [assets, setAssets] = useState([]);
  const [dataState, setDataState] = useState({ data: [] });
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [loaderState, setLoaderState] = useState(false);
  const [totalPageState, setTotalPageState] = useState(0);
  const [editState, setEditState] = useState({ damage: {}, valid: false });
  // valid defaults to true (no objection); a detail form sets it false only when its own required fields are empty.
  const [detailsState, setDetailsState] = useState({ damage: {}, valid: true });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [groupByComponent, setGroupByComponent] = useState(true);
  const [sendaiTypes, setSendaiTypes] = useState([]);
  const { t } = useTranslation();
  // Parent enterprise — only needed by BuildingComponentDamageForm to know
  // whether the survey is on a cultural-heritage church (then it renders
  // DamageChurchForm instead of the generic damage_class chooser). Fetched
  // lazily and only for the kinds where subtype actually matters
  // (NonOrdinaryBuilding); null for everything else.
  const [parentEnterprise, setParentEnterprise] = useState(null);

  const isParentEditMode = props.isEditMode || false;

  const handleTableChange = (pagination, filters, sorter) => {
    setListState({ pagination: pagination, filters: filters, sorter: sorter });
  };

  const saveDamage = () => {
    if (!isEditMode || !editState.valid || detailsState.valid === false) {
      return;
    }

    const isNewDamage = editState.damage.id === -1;

    const toSend = {
      parentId: props.record.survey.id,
      ...editState.damage,
      ...detailsState.damage,
      id: isNewDamage ? null : editState.damage.id  // Always use editState's id to prevent stale detailsState from overwriting it
    }
    console.log("[DAMAGE] = " + JSON.stringify(toSend));
    props.startLoading();

    axios
      .post(process.env.REACT_APP_API_ENDPOINT + "/damages/save", toSend)
      .then(({ data }) => {
        setSelectedRowKey(data.id);
        setIsEditMode(false);
        reloadDamages();
        setTimeout(() => {
          props.stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          props.stopLoading("failure");
        }, 1000);
      });
  };

  const newDamage = () => {
    const newDamage = { id: -1 };
    setDataState({
      data: [newDamage, ...dataState.data]
    });
    setEditState({ damage: newDamage, valid: false });
    setDetailsState({ damage: {}, valid: true });
    setIsEditMode(true);
    setSelectedRowKey(-1);
  };

  const cancelEdit = () => {
    // If we're canceling a new damage, remove it from the temporary display
    if (editState.damage.id === -1) {
      setDataState({
        data: dataState.data.filter(damage => damage.id !== -1)
      });
      setSelectedRowKey(null);
      setEditState({ damage: {}, valid: false });
    } else {
      // If we're canceling an edit to an existing damage, remember its ID
      const damageId = editState.damage.id;
      // We'll reselect this damage after reloading
      setSelectedRowKey(damageId);
    }

    setDetailsState({ damage: {}, valid: true });
    setIsEditMode(false);
    reloadDamages();
  };

  const reloadDamages = () => {
    // Remember the currently selected damage ID before reloading
    const currentSelectedId = selectedRowKey;

    setLoaderState(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/damages/", {
        params: {
          parentId: props.record.survey.id,
          pageSize: listState.pagination.pageSize,
          pageNumber: listState.pagination.current,
          sortField: listState.sorter.field,
          sortOrder: listState.sorter.order,
          ...listState.filters
        }
      })
      .then(({ data }) => {
        setDataState({ data: data.data });
        setTotalPageState(data.totalCount);

        // Clear selection if there's no data
        if (data.data.length === 0) {
          setEditState({ damage: {}, valid: false });
          setSelectedRowKey(null);
        }
        // If there was a selected damage, find it in the new data and update editState
        else if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedDamage = data.data.find(damage => damage.id === currentSelectedId);
          if (selectedDamage) {
            setEditState({ damage: selectedDamage, valid: true });
            setSelectedRowKey(currentSelectedId);
          }
        }
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setLoaderState(false);
      });
  };

  const editDamage = id => {
    const toEdit = dataState.data.find(i => i.id === id);
    // Create a deep copy to avoid reference issues
    const toEditCopy = JSON.parse(JSON.stringify(toEdit));
    setEditState({ damage: toEditCopy, valid: true });
    setDetailsState({ damage: {}, valid: true });
    setIsEditMode(true);
    setSelectedRowKey(id);
  };

  const showDamage = id => {
    const toShow = dataState.data.find(i => i.id === id);
    setEditState({ damage: toShow, valid: true });
    setDetailsState({ damage: {}, valid: true });
    setIsEditMode(false);
    setSelectedRowKey(id);
  };

  const deleteDamage = id => {
    if (isEditMode || !isParentEditMode) {
      return; // Prevent deletion while in edit mode
    }

    const toDelete = dataState.data.find(i => i.id === id);
    props.startLoading();
    axios
      .delete(process.env.REACT_APP_API_ENDPOINT + "/damages/delete", {
        params: {
          id: toDelete.id
        }
      })
      .then(({ data }) => {
        reloadDamages();
        setEditState({ damage: {}, valid: false });
        setSelectedRowKey(null);
        setTimeout(() => {
          props.stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          props.stopLoading("failure");
        }, 1000);
      });
  };

  const damageChange = (newRecord) => {
    var newState = { ...editState, ...newRecord };
    setEditState(newState);
  };

  const damageDetailsChange = newRecord => {
    var newState = { ...detailsState, ...newRecord };
    setDetailsState(newState);
  };

  const components = {
    'empty': EmptyDamageForm,
    'structure': FixedAssetDamageForm,
    'infrastructure': FixedAssetDamageForm,
    'machinery': FixedAssetDamageForm,
    'land': FixedAssetDamageForm,
    'material': FixedAssetDamageForm,
    'provision': ProvisionDamageForm,
    'livestock': LivestockDamageForm,
    'crop': AgricultureProductDamageForm,
    'stored': AgricultureProductDamageForm,
    'staff': StaffDamageForm,
    //TLC
    'tlc_service': TlcServiceDamageForm,
    'tlc_component': TlcComponentDamageForm,
    //TRANSPORT
    'transport_service': TransportServiceDamageForm,
    'transport_component': TransportComponentDamageForm,
    //ELECTRICAL
    'electrical_service': ElectricalServiceDamageForm,
    'electrical_component': ElectricalComponentDamageForm,
    //Building
    'building_component': BuildingComponentDamageForm,
    'artistic_good':      DamageArtisticGoodForm,
  };

  const getComponents = () => {
    const node = editState.damage ? search(editState.damage.componentId, assets.data, 'value',) : null;
    const name = node ? node.type : node;
    const DetailsComponent = components[name] || EmptyDamageForm;
    return <DetailsComponent record={editState} parentCallback={damageDetailsChange} isEditMode={isEditMode} parentEnterprise={parentEnterprise} />
  };

  useEffect(() => {
    // If parent survey changes or is deleted, reset damage state
    if (!props.record.survey || !props.record.survey.id || props.record.survey.id === -1) {
      setEditState({ damage: {}, valid: false });
      setSelectedRowKey(null);
      setIsEditMode(false);
      setDataState({ data: [] });
      return;
    }

    reloadDamages();
  }, [listState, props.record.survey]);

  useEffect(() => {
    if (!props.type || !props.type.value) {
      setSendaiTypes([]);
      return;
    }
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/damages/enums/sendai-indicators")
      .then(({ data }) => {
        setSendaiTypes(filterSendaiByEnterprise(data, props.type.value));
      })
      .catch(err => console.log(err));
  }, [props.type]);

  useEffect(() => {
    // Don't try to load assets if survey doesn't exist or has no enterprise
    if (!props.record.survey || !props.record.survey.enterpriseId) {
      setAssets({ data: [] });
      return;
    }

    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/" + props.type.apiPath + "/components/", {
        params: { parentId: props.record.survey.enterpriseId }
      })
      .then(({ data }) => {
        const options = data.data.map(c => {
          return { value: c.id, label: c.name, type: c.typeName };
        });
        setAssets({ data: options });
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [props.record.survey.enterpriseId, props.type]);

  // Fetch the parent enterprise once per survey so dispatched damage forms
  // can branch on its kind/subtype. Only does the call for non-ordinary
  // buildings — the only enterprise kind that currently exposes subtype
  // semantics (CULTURAL_HERITAGE_CHURCH triggers the church damage form).
  useEffect(() => {
    if (props.type.value !== "NON_ORDINARY_BUILDING"
        || !props.record.survey
        || !props.record.survey.enterpriseId) {
      setParentEnterprise(null);
      return;
    }
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/non-ordinary-building/" + props.record.survey.enterpriseId)
      .then(({ data }) => setParentEnterprise(data))
      .catch(err => {
        console.log("Could not load parent enterprise:", err);
        setParentEnterprise(null);
      });
  }, [props.record.survey.enterpriseId, props.type]);

  // Reset edit mode if parent goes into edit mode
  useEffect(() => {
    if (isParentEditMode && isEditMode) {
      cancelEdit();
    }
  }, [isParentEditMode]);

  const sortedData = useMemo(() => {
    if (!groupByComponent) return dataState.data;
    return [...dataState.data].sort((a, b) => (a.componentId ?? 0) - (b.componentId ?? 0));
  }, [dataState.data, groupByComponent]);

  const computeRowSpan = (idx, componentId) => {
    if (!groupByComponent) return 1;
    if (idx > 0 && sortedData[idx - 1].componentId === componentId) return 0;
    let span = 1;
    while (idx + span < sortedData.length && sortedData[idx + span].componentId === componentId) {
      span++;
    }
    return span;
  };

  const translateTypePath = (text) => text
    ? text.split('/').map(seg => {
        const key = `componentType.${seg.trim()}`;
        return i18n.exists(key) ? t(key) : seg;
      }).join(' / ')
    : text;

  // Required damage fields, surfaced in the Save tooltip when still empty.
  const requiredDamageFields = [
    { key: 'componentId', label: 'Asset' },
    { key: 'description', label: 'Description' }
  ];
  const missingDamageFields = isEditMode
    ? requiredDamageFields
        .filter(({ key }) => {
          const value = editState.damage?.[key];
          return !value || (typeof value === 'string' && value.trim() === '');
        })
        .map(({ label }) => label)
    : [];
  const saveDisabledReasons = [];
  if (missingDamageFields.length > 0) {
    saveDisabledReasons.push(`Required fields still empty: ${missingDamageFields.join(', ')}`);
  }
  if (detailsState.valid === false) {
    saveDisabledReasons.push('Complete the damage details');
  }
  const saveDisabledReason = saveDisabledReasons.join(' · ');

  const columns = [
    {
      title: "Asset name",
      dataIndex: "componentName",
      onCell: (record, idx) => ({ rowSpan: computeRowSpan(idx, record.componentId) }),
    },
    {
      title: "Asset type",
      dataIndex: "componentTypeDescriptionPath",
      // Server returns a path: each segment may be a raw enum name or a legacy
      // description. Translate per segment via translation.json (componentType.<name>).
      render: translateTypePath,
      onCell: (record, idx) => ({ rowSpan: computeRowSpan(idx, record.componentId) }),
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: { showTitle: false },
      render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
    },
    {
      title: "Damage Date",
      dataIndex: "date",
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : ''
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={!isParentEditMode || isEditMode}
            onClick={(e) => {
              e.stopPropagation();
              editDamage(record.id);
            }}
          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={!isParentEditMode || isEditMode}
            onClick={(e) => {
              e.stopPropagation();
              deleteDamage(record.id);
            }}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>
            {props.record.survey.enterpriseVat
              ? "Survey damages for " + props.record.survey.enterpriseVat
              : "Survey damages"}
          </Title>
        </Col>
        <Col>
          <Button
            disabled={isEditMode}
            onClick={() => setGroupByComponent(g => !g)}>
            {groupByComponent ? 'Flat view' : 'Group by component'}
          </Button>
          <Button key="1"
            style={{ marginLeft: 8 }}
            disabled={!isParentEditMode || isEditMode}
            onClick={() => reloadDamages()}>
            Refresh
          </Button>

          <Button key="2"
            style={{ marginLeft: 8 }}
            disabled={!isParentEditMode || isEditMode}
            onClick={() => newDamage()}>
            New
          </Button>
          <Tooltip title={saveDisabledReason}>
            <span style={{ marginLeft: 8 }}>
              <Button
                key="3"
                type="primary"
                disabled={!isEditMode || !editState.valid || detailsState.valid === false}
                onClick={() => saveDamage()}>
                Save
              </Button>
            </span>
          </Tooltip>
          <Button
            key="4"
            style={{ marginLeft: 8 }}
            onClick={() => cancelEdit()}
            disabled={!isEditMode}>
            Cancel
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={record => record.id}
            dataSource={sortedData}
            pagination={groupByComponent ? false : { ...listState.pagination, total: totalPageState }}
            loading={loaderState}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                if (!isEditMode) {
                  showDamage(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isEditMode ? { cursor: 'not-allowed' } : {}  // Changed from: (!isParentEditMode || isEditMode)
            })}
            rowClassName={(damage) => damage.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      {selectedRowKey !== null && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <SurveysDamageForm
                eventId={props.record.survey.eventId}
                assets={assets}
                sendaiTypes={sendaiTypes}
                record={editState}
                parentCallback={damageChange}
                isEditMode={isEditMode}
              />
            </Col>
            <Col span={12}>
              {getComponents()}
            </Col>
          </Row>
          {editState.damage && editState.damage.id && editState.damage.id !== -1 && (
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <SurveysDamageEconomicLoss
                  damageId={editState.damage.id}
                  startLoading={props.startLoading}
                  stopLoading={props.stopLoading}
                  isEditMode={isEditMode}
                />
                <SurveysDamageUploads
                  damageId={editState.damage.id}
                  isEditMode={isEditMode}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    startLoading: () => dispatch({ type: "START_LOADING" }),
    stopLoading: val => dispatch({ type: "STOP_LOADING", status: val })
  };
};

export default connect(null, mapDispatchToProps)(SurveyDamagesInfo);